import { useState, useEffect } from 'react';
import { Link, useNavigate, useLocation, useSearchParams } from 'react-router-dom';
import { useAuthStore } from '../store/auth';
import { AuthShell, AuthCard, AuthInput, AuthButton, AuthError } from '../components/AuthShell';

const REFERRAL_KEY = 'squishy_pending_referral';

export function LoginPage() {
  const nav = useNavigate();
  const loc = useLocation();
  const [searchParams] = useSearchParams();
  const { signIn, loading, error, clearError } = useAuthStore();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const params = new URLSearchParams(loc.search);
  const sessionExpired = params.get('reason') === 'session_expired';
  const returnUrl = searchParams.get('returnUrl') ?? '';
  const joinMatch = returnUrl.match(/\/groups\/join\/([A-Z0-9]+)/i);
  const joinRoomCode = joinMatch?.[1] ?? null;

  // Capture referral param
  useEffect(() => {
    const ref = searchParams.get('ref');
    if (ref) localStorage.setItem(REFERRAL_KEY, ref);
  }, [searchParams]);

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    clearError();
    const r = await signIn(email.trim().toLowerCase(), password);
    if (r.ok) {
      const to = (loc.state as any)?.from ?? searchParams.get('returnUrl') ?? '/';
      nav(to, { replace: true });
    } else if (r.nextStep === 'CONFIRM_SIGN_UP') {
      nav('/confirm', { state: { email } });
    }
  };

  return (
    <AuthShell>
      <AuthCard
        title={joinRoomCode ? 'Sign in to join' : 'Welcome back'}
        subtitle={joinRoomCode ? undefined : 'Sign in to keep your streak alive 🔥'}
      >
        {joinRoomCode && (
          <div className="mb-4 bg-teal-50 border border-teal-200 rounded-xl p-3 text-center">
            <div className="text-xs text-teal-600 font-medium mb-1">You've been invited to a study group</div>
            <div className="text-lg font-mono font-bold text-squishy-dark tracking-[0.2em]">{joinRoomCode}</div>
            <div className="text-xs text-slate-500 mt-1">Sign in and you'll join automatically</div>
          </div>
        )}
        <form onSubmit={submit} className="space-y-4">
          <AuthInput
            type="email"
            label="Email"
            value={email}
            onChange={setEmail}
            autoComplete="email"
            required
            autoFocus
          />
          <AuthInput
            type="password"
            label="Password"
            value={password}
            onChange={setPassword}
            autoComplete="current-password"
            required
          />
          {sessionExpired && <AuthError message="You were signed out because your account was logged in on another device." />}
          {error && <AuthError message={error} />}
          <AuthButton type="submit" loading={loading}>Sign in</AuthButton>
        </form>
        <div className="mt-5 flex items-center justify-between text-sm">
          <Link to="/forgot" className="text-squishy-primary hover:underline">Forgot password?</Link>
          <Link to={`/signup${(() => {
            const p = new URLSearchParams();
            if (searchParams.get('ref')) p.set('ref', searchParams.get('ref')!);
            if (returnUrl) p.set('returnUrl', returnUrl);
            const s = p.toString();
            return s ? `?${s}` : '';
          })()}`} className="text-squishy-primary hover:underline">Create account</Link>
        </div>
        <p className="mt-4 text-center text-xs text-slate-400">
          <Link to="/privacy" className="hover:text-squishy-primary hover:underline">Privacy Policy</Link>
        </p>
      </AuthCard>
    </AuthShell>
  );
}
